/**
 * Identidad del build: que codigo esta corriendo y en que entorno.
 * Los valores los inyecta Vite en build time (ver `define` en vite.config.ts).
 */

/** Marcador para un build sin estampar: es mas honesto que un string vacio. */
const DESCONOCIDO = 'desconocido';

/** Largo del SHA corto, igual al que usa `git log --oneline`. */
const LARGO_SHA_CORTO = 7;

export interface RawBuildStamp {
  version?: string;
  commit?: string;
  builtAt?: string;
  environment?: string;
}

export interface BuildInfo {
  version: string;
  commit: string;
  builtAt: string;
  environment: string;
}

/**
 * Un valor solo cuenta si trae contenido real. Un `git rev-parse` que falla en CI
 * inyecta '' y dejaria un stamp hueco en vez de avisar que no hay dato.
 */
function normalizar(valor: string | undefined): string | undefined {
  const limpio = valor?.trim();
  return limpio ? limpio : undefined;
}

export function resolveBuildInfo(raw: RawBuildStamp): BuildInfo {
  const commit = normalizar(raw.commit);

  return {
    version: normalizar(raw.version) ?? DESCONOCIDO,
    commit: commit ? commit.slice(0, LARGO_SHA_CORTO) : DESCONOCIDO,
    builtAt: normalizar(raw.builtAt) ?? DESCONOCIDO,
    environment: normalizar(raw.environment) ?? DESCONOCIDO,
  };
}

/** Etiqueta compacta para mostrar en la UI o mandar a logs: `0.5.0+a3f9c21 (qa)`. */
export function formatBuildStamp(info: BuildInfo): string {
  return `${info.version}+${info.commit} (${info.environment})`;
}

/**
 * Identidad del build actual, resuelta una sola vez al cargar el modulo.
 * El `typeof` no es decorativo: sin `define` el identificador no existe y leerlo
 * directo tira ReferenceError (es lo que pasa al correr los tests).
 */
export const buildInfo: BuildInfo = resolveBuildInfo({
  version:
    typeof __BUILD_VERSION__ === 'string' ? __BUILD_VERSION__ : undefined,
  commit: typeof __BUILD_COMMIT__ === 'string' ? __BUILD_COMMIT__ : undefined,
  builtAt: typeof __BUILD_AT__ === 'string' ? __BUILD_AT__ : undefined,
  environment: typeof __BUILD_ENV__ === 'string' ? __BUILD_ENV__ : undefined,
});
